import { useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import { Badge, Banner, PageLoading, TextInput, Select } from '../../components/ui';
import type { Customer, CustomerType, Area, PriceList } from '../../types';

interface Props {
  customerId: string;
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Customer detail — view + inline edit.
 *
 * Edit mode swaps the read-only `<dl>` for form inputs. On save it
 * PATCHes `/customers/:id` with the changed fields.
 *
 * `retiredFields` (values from removed form fields) are always read-only.
 */
export default function CustomerDetail({ customerId, onClose, onChanged }: Props) {
  const { can } = useAuth();
  const toast = useToast();

  const canEdit = can('company_admin', 'platform_admin');
  const canDelete = can('manager', 'company_admin', 'platform_admin');

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [types, setTypes] = useState<CustomerType[]>([]);
  const [routes, setRoutes] = useState<Area[]>([]);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  /* ── edit state ─────────────────────────────────────────────── */
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState('');
  const [formRouteId, setFormRouteId] = useState('');
  const [formStatus, setFormStatus] = useState('');
  const [formTypeId, setFormTypeId] = useState('');
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formCode, setFormCode] = useState('');
  const [formPriceListId, setFormPriceListId] = useState('');
  const [formLatitude, setFormLatitude] = useState('');
  const [formLongitude, setFormLongitude] = useState('');

  /* ── load customer ──────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<Customer>(`/customers/${customerId}`);
        if (!cancelled) setCustomer(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiClientError ? err.message : 'Could not load this customer.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  /* ── load types + routes for the dropdowns ──────────────────── */
  useEffect(() => {
    if (!canEdit) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [typesData, routesData, priceListData] = await Promise.all([
          api.get<CustomerType[]>('/customers/types'),
          api.get<Area[]>('/customers/routes'),
          api.get<PriceList[]>('/orders/price-lists'),
        ]);
        if (!cancelled) {
          setTypes(typesData);
          setRoutes(routesData);
          setPriceLists(priceListData);
        }
      } catch {
        // Editing is a convenience; the rest of the screen still works.
      }
    })();
    return () => { cancelled = true; };
  }, [canEdit]);

  /* ── edit helpers ────────────────────────────────────────────── */
  function startEditing() {
    if (!customer) return;
    setFormName(customer.name);
    setFormRouteId(customer.route?.id ?? '');
    setFormStatus(customer.status);
    setFormTypeId(customer.customerType?.id ?? '');
    const vals: Record<string, string> = {};
    for (const [key, field] of Object.entries(customer.fields)) {
      vals[key] = typeof field.value === 'string' ? field.value : String(field.value ?? '');
    }
    setFormValues(vals);
    setFormCode(customer.code ?? '');
    setFormPriceListId(customer.priceList?.id ?? '');
    setFormLatitude(customer.location?.latitude?.toString() ?? '');
    setFormLongitude(customer.location?.longitude?.toString() ?? '');
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
  }

  async function save() {
    if (!formName.trim()) {
      toast.error('Name is required.');
      return;
    }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: formName.trim(),
        routeId: formRouteId || null,
        status: formStatus,
        customerTypeId: formTypeId || null,
        code: formCode.trim() || null,
        priceListId: formPriceListId || null,
        latitude: formLatitude ? Number(formLatitude) : null,
        longitude: formLongitude ? Number(formLongitude) : null,
        values: formValues,
      };
      const updated = await api.patch<Customer>(`/customers/${customerId}`, body);
      setCustomer(updated);
      setEditing(false);
      toast.success('Customer updated.');
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  }

  /* ── delete ─────────────────────────────────────────────────── */
  async function remove() {
    setBusy(true);
    try {
      await api.del(`/customers/${customerId}`);
      toast.success(`${customer?.name ?? 'Customer'} removed.`);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove.');
      setBusy(false);
    }
  }

  const retired = customer?.retiredFields ?? [];

  return (
    <Modal
      title={customer?.name ?? 'Customer'}
      subtitle={customer?.route?.name ?? undefined}
      onClose={editing ? () => {} : onClose}
      wide
      footer={(
        <>
          {editing ? (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelEditing}
                disabled={saving}
              >
                Cancel
              </button>
              <div className="grow" />
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void save()}
                disabled={saving}
              >
                {saving ? <span className="spinner" /> : null}
                Save changes
              </button>
            </>
          ) : (
            <>
              {canDelete && customer ? (
                <button
                  type="button"
                  className="btn btn-danger-ghost"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Icon name="trash" size={15} /> Remove
                </button>
              ) : null}
              <div className="grow" />
              {canEdit && customer ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={startEditing}
                >
                  <Icon name="edit" size={15} /> Edit
                </button>
              ) : null}
              <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            </>
          )}
        </>
      )}
    >
      {error ? <Banner kind="danger">{error}</Banner> : null}
      {!customer && !error ? <PageLoading rows={3} /> : null}

      {customer ? (
        <div className="col gap-24">
          {!editing ? (
            /* ── Read-only view ──────────────────────────── */
            <>
              <div className="row gap-8 wrap">
                <Badge tone={customer.status === 'active' ? 'success' : 'warning'}>
                  {customer.status.replace('_', ' ')}
                </Badge>
                {customer.code ? <Badge>Code {customer.code}</Badge> : null}
                <Badge>Form v{customer.formVersion}</Badge>
              </div>

              <dl className="kv">
                <dt>Name</dt>
                <dd>{customer.name}</dd>

                <dt>Area / Route</dt>
                <dd>{customer.route?.name ?? '—'}</dd>

                <dt>Type</dt>
                <dd>{customer.customerType?.name ?? '—'}</dd>

                <dt>Price List</dt>
                <dd>{customer.priceList?.name ?? '—'}</dd>

                {Object.entries(customer.fields).map(([key, field]) => (
                  <div key={key} style={{ display: 'contents' }}>
                    <dt>{field.label}</dt>
                    <dd className={field.type === 'gstin' || field.type === 'pan' ? 'mono' : undefined}>
                      {field.display || '—'}
                    </dd>
                  </div>
                ))}

                {customer.location ? (
                  <>
                    <dt>Location</dt>
                    <dd className="mono">
                      {customer.location.latitude.toFixed(5)}, {customer.location.longitude.toFixed(5)}
                    </dd>
                  </>
                ) : null}

                <dt>Added by</dt>
                <dd>
                  {customer.createdBy ?? '—'}
                  <span className="faint small"> · {new Date(customer.createdAt).toLocaleString()}</span>
                </dd>
              </dl>
            </>
          ) : (
            /* ── Edit mode ───────────────────────────────── */
            <div className="col gap-16">
              <TextInput
                label="Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
              />

              <Select
                label="Area / Route"
                value={formRouteId}
                onChange={(e) => setFormRouteId(e.target.value)}
              >
                <option value="">— No area —</option>
                {routes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {'  '.repeat(r.depth)}{r.name}
                  </option>
                ))}
              </Select>

              <Select
                label="Status"
                value={formStatus}
                onChange={(e) => setFormStatus(e.target.value)}
              >
                <option value="active">Active</option>
                <option value="pending_approval">Pending approval</option>
                <option value="archived">Archived</option>
              </Select>

              <Select
                label="Type"
                value={formTypeId}
                onChange={(e) => setFormTypeId(e.target.value)}
              >
                <option value="">— No type —</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>

              <TextInput
                label="Code"
                value={formCode}
                onChange={(e) => setFormCode(e.target.value)}
                placeholder="Optional unique code"
              />

              <Select
                label="Price List"
                value={formPriceListId}
                onChange={(e) => setFormPriceListId(e.target.value)}
              >
                <option value="">— No price list —</option>
                {priceLists.map((pl) => (
                  <option key={pl.id} value={pl.id}>{pl.name}</option>
                ))}
              </Select>

              {Object.entries(customer.fields).map(([key, field]) => (
                <TextInput
                  key={key}
                  label={field.label}
                  value={formValues[key] ?? ''}
                  onChange={(e) =>
                    setFormValues((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                />
              ))}

              <div className="row gap-12">
                <TextInput
                  label="Latitude"
                  value={formLatitude}
                  onChange={(e) => setFormLatitude(e.target.value)}
                  placeholder="e.g. 28.6139"
                />
                <TextInput
                  label="Longitude"
                  value={formLongitude}
                  onChange={(e) => setFormLongitude(e.target.value)}
                  placeholder="e.g. 77.2090"
                />
              </div>
            </div>
          )}

          {/* Retired fields — always read-only */}
          {retired.length ? (
            <div>
              <div className="row gap-8" style={{ marginBottom: 8 }}>
                <h3>Kept from an earlier form</h3>
              </div>
              <Banner kind="info">
                These were collected under fields that are no longer on the form.
                They are kept on the record and still come out in exports.
              </Banner>
              <dl className="kv" style={{ marginTop: 12 }}>
                {retired.map((field) => (
                  <div key={field.key} style={{ display: 'contents' }}>
                    <dt className="mono">{field.key}</dt>
                    <dd className="muted">
                      {typeof field.value === 'object'
                        ? JSON.stringify(field.value)
                        : String(field.value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}
        </div>
      ) : null}

      {confirmDelete ? (
        <Modal
          title={`Remove ${customer?.name}?`}
          onClose={() => setConfirmDelete(false)}
          dismissable={false}
          footer={(
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmDelete(false)}
              >
                Keep it
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={busy}
                onClick={() => void remove()}
              >
                {busy ? <span className="spinner" /> : null}
                Remove customer
              </button>
            </>
          )}
        >
          <Banner kind="warning">
            The shop stops appearing in the app and in reports. The record itself is kept,
            so this can be reversed by support if it was a mistake.
          </Banner>
        </Modal>
      ) : null}
    </Modal>
  );
}
